import type { StaffCatalogApi, StaffItineraryApi, StaffRequestsApi } from "@lafayette/api-client";
import { createContext, type ReactNode, useContext } from "react";

// What the itinerary pages need from outside: the typed staff itinerary client, the two read paths the plan is joined with (the
// catalog, for "is this item still published", and the request read path, for the LIVE state of a linked request), and the clock.
// The admin app supplies the real ones (`admin-runtime.ts`); a test supplies fakes.

export type ItineraryRuntime = Readonly<{
  api: StaffItineraryApi;
  catalog: Pick<StaffCatalogApi, "listItems">;
  requests: Pick<StaffRequestsApi, "get">;
  now: () => Date;
  /** Milliseconds between reads of a linked request's state. `0` reads on mount, on focus and after a command only. */
  requestRefreshMs: number;
}>;

const RuntimeContext = createContext<ItineraryRuntime | null>(null);

export function ItineraryRuntimeProvider({
  value,
  children,
}: Readonly<{ value: ItineraryRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function useItineraryRuntime(): ItineraryRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("The itinerary pages need an ItineraryRuntimeProvider.");
  return runtime;
}
